// Bulk-action bar, shown above the table while rows are checked.
//
// Button treatment follows design-system/components/DataTable's selection toolbar:
// size="sm" solid buttons rather than ghost text, so the actions read as controls.
// The bar itself uses the theme's brand-tinted tokens, which carry _dark pairs.

import { Box, Button, HStack, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import { MdClose, MdDelete, MdToggleOff, MdToggleOn } from 'react-icons/md';

interface WorkflowBulkBarProps {
  count: number;
  /** Which tab the selection came from — decides whether the toggle activates or deactivates. */
  showingActive: boolean;
  isBusy: boolean;
  onToggleStatus: () => void;
  onDelete: () => void;
  onClear: () => void;
}

export function WorkflowBulkBar({
  count,
  showingActive,
  isBusy,
  onToggleStatus,
  onDelete,
  onClear,
}: WorkflowBulkBarProps) {
  const labelColor = useColorModeValue('brand.700', 'brand.100');

  return (
    <HStack
      px={4}
      py={3}
      spacing={3}
      bg="interactive-brand-hover"
      borderWidth="1px"
      borderColor="border-brand-light"
      borderRadius="lg"
    >
      <Text
        data-testid="wfl-bulk-count"
        fontSize="sm"
        fontWeight="semibold"
        color={labelColor}
        whiteSpace="nowrap"
      >
        {count} selected
      </Text>

      <Box flex={1} />

      <Button
        size="sm"
        colorScheme="brand"
        leftIcon={<Icon as={showingActive ? MdToggleOff : MdToggleOn} />}
        onClick={onToggleStatus}
        isDisabled={isBusy}
        data-testid="wfl-bulk-toggle-btn"
      >
        {showingActive ? 'Deactivate' : 'Activate'}
      </Button>
      {/* Explicit colours, not colorScheme="red": the theme hard-codes bg: brand.500 on
          Button.variants.solid (client/theme/index.ts:144), so colorScheme is ignored and
          a destructive button would render brand teal. status-error is the same token
          BrandedQRCard used to mark destructive actions. */}
      <Button
        size="sm"
        bg="status-error"
        color="white"
        _hover={{ bg: 'error.500' }}
        _active={{ bg: 'error.600' }}
        leftIcon={<Icon as={MdDelete} />}
        onClick={onDelete}
        isDisabled={isBusy}
        data-testid="wfl-bulk-delete-btn"
      >
        Delete
      </Button>
      <Button
        size="sm"
        variant="outline"
        borderColor="border-brand-light"
        color={labelColor}
        _hover={{ bg: 'bg-surface' }}
        leftIcon={<Icon as={MdClose} />}
        onClick={onClear}
        isDisabled={isBusy}
        data-testid="wfl-bulk-clear-btn"
      >
        Clear
      </Button>
    </HStack>
  );
}
